import { useNavigate } from "@entry/navigation";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import { AmountAdjuster } from "@components/AmountAdjuster";
import { SidecarBadge, sidecarContributionFrequencyOptions } from "@components/Goals/TrumpSidecar";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { SegmentedControl } from "@components/SegmentedControl";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { DEFAULT_CONTRIBUTE_AFTER_18, DEFAULT_MULTIPLIER, DEFAULT_PROJECTED_AGE } from "@constants";
import { Flow, Routes, SipFrequency } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useGoalFlow, useTrumpSidecar } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  calculateProjectedAmount,
  calculateWithoutSidecar,
  convertDollarToCents,
  formatCurrency,
  fromMonthlySidecarContribution,
  getSidecarInputs,
} from "@utils";

const MIN_AMOUNT = 1;
const DEFAULT_MAX_AMOUNT = 250;
const ROUND_UP_TO = 50;
const STEP = 1;

export const TrumpSidecarRecommendedContributionRoute = () => {
  const navigate = useNavigate();

  const {
    goalProps: {
      flow,
      trumpAccountBalanceInCents,
      trumpContributionAmountInCents,
      trumpContributionFrequency,
      incomeAt18,
    },
    setGoalProps,
  } = useGoalFlow();

  const { firstName, isSelf, dateOfBirth, currentAge, taxState } = useTrumpSidecar();

  const [selectedFrequency, setSelectedFrequency] = useState(SipFrequency.Weekly);

  // Fall back to the first person copy when no name is available.
  const shouldUseSelfCopy = isSelf || !firstName;

  const recommendedMonthly = useMemo(() => {
    const sidecarInputs = getSidecarInputs(
      dateOfBirth,
      trumpContributionAmountInCents,
      trumpContributionFrequency,
      taxState,
      trumpAccountBalanceInCents,
      incomeAt18,
    );

    if (!sidecarInputs) return 0;

    return calculateWithoutSidecar(sidecarInputs).monthlySidecarSavings;
  }, [
    dateOfBirth,
    taxState,
    trumpAccountBalanceInCents,
    trumpContributionAmountInCents,
    trumpContributionFrequency,
    incomeAt18,
  ]);

  const recommendedForFrequency = Math.ceil(
    fromMonthlySidecarContribution(recommendedMonthly, selectedFrequency),
  );

  const maxAmount = Math.max(
    DEFAULT_MAX_AMOUNT,
    Math.ceil(recommendedForFrequency / ROUND_UP_TO) * ROUND_UP_TO,
  );

  const recommendedAmount = Math.max(MIN_AMOUNT, Math.min(maxAmount, recommendedForFrequency));

  const [selectedAmount, setSelectedAmount] = useState(recommendedAmount);
  const [hasAdjustedAmount, setHasAdjustedAmount] = useState(false);

  // The beneficiary date of birth comes from the user query, so the
  // recommendation can land after the first render. Keep following it until the
  // user picks their own amount.
  /* eslint-disable react-hooks/set-state-in-effect -- sync derived recommendation to state */
  useEffect(() => {
    if (hasAdjustedAmount) return;

    setSelectedAmount(recommendedAmount);
  }, [hasAdjustedAmount, recommendedAmount]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleAmountChange = (nextAmount: number) => {
    setHasAdjustedAmount(true);
    setSelectedAmount(nextAmount);
  };

  const handleFrequencyChange = (key: string) => {
    const newFrequency = key as SipFrequency;

    setSelectedFrequency(newFrequency);
    setSelectedAmount(
      Math.max(
        MIN_AMOUNT,
        Math.min(
          maxAmount,
          Math.ceil(fromMonthlySidecarContribution(recommendedMonthly, newFrequency)),
        ),
      ),
    );
  };

  const handleSubmit = () => {
    const targetYear = DEFAULT_PROJECTED_AGE - currentAge + dayjs().year();

    const targetAmount = calculateProjectedAmount({
      amount: selectedAmount,
      recurringDepositType: selectedFrequency,
      currentAge,
      projectedAge: DEFAULT_PROJECTED_AGE,
      multiplier: DEFAULT_MULTIPLIER,
      contributeAfter18: DEFAULT_CONTRIBUTE_AFTER_18,
    });

    setGoalProps({
      sidecarContributionAmountInCents: convertDollarToCents(selectedAmount),
      sidecarContributionFrequency: selectedFrequency,
      targetYear,
      targetAmount,
    });

    navigate(Routes.AccountType);
  };

  const props: OnboardingLayoutProps = {
    title: shouldUseSelfCopy
      ? i18n.t(Translations.TrumpSidecarRecommendedContributionRouteTitleSelf)
      : i18n.t(Translations.TrumpSidecarRecommendedContributionRouteTitle, {
          firstName,
        }),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.TrumpSidecarRecommendedContributionRouteCTA, {
          amount: formatCurrency(selectedAmount),
        }),
        onClick: handleSubmit,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <SidecarBadge>
        {i18n.t(Translations.TrumpSidecarRecommendedContributionRouteBadgeLabel)}
      </SidecarBadge>
      <Spacing size={SpacingSizes.M} />

      <AmountAdjuster
        amount={selectedAmount}
        onAmountChange={handleAmountChange}
        min={MIN_AMOUNT}
        max={maxAmount}
        step={STEP}
      />
      <Spacing size={SpacingSizes.M} />

      <SegmentedControl
        options={sidecarContributionFrequencyOptions}
        selectedKey={selectedFrequency}
        onSelect={handleFrequencyChange}
      />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
